// AGENT-REPORTING (13 §Reporting duties): a seat measurable's reporting status wherever its row
// shows — Cadence's grid, the chart's seat panel, the DE profile's scorecard, Setup › KPI
// reporting (and KPI-ASSIGN's KPI editor rows). Next due (relative, and the instant in company
// time), Late (danger), Blocked (warning, with the reason), Reported, the last report (value,
// period, who — the Agent mark when a run reported it) and Pending review (a link to the
// approvals queue filtered to KPI reviews). Props are the scorecard row's own fields.
import { Badge, buttonVariants } from "@companyos/ui";
import { Link } from "@tanstack/react-router";
import { GaugeIcon } from "lucide-react";
import { type ReactNode, useContext } from "react";
import type { ScorecardMetric } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { AgentReportMark } from "../agent-report";
import { isManager } from "../company";
import { SessionContext } from "../use-feature";

export type ReportingStatus = NonNullable<ScorecardMetric["reporting"]>;
export type LastReport = NonNullable<ScorecardMetric["last_report"]>;
export type PendingReview = NonNullable<ScorecardMetric["pending_review"]>;

export type ReportingStatusProps = {
  reporting?: ReportingStatus | null | undefined;
  lastReport?: LastReport | null | undefined;
  pendingReview?: PendingReview | null | undefined;
  /** One badge (the most urgent), for dense rows. */
  compact?: boolean | undefined;
  /**
   * SEAT-DRAWER-CLICKS: how Pending review links to the KPI reviews. `false` draws the badge
   * alone (a drawer row, whose click opens the KPI modal); a node marks the link as a page link
   * and draws that node inside it, at the end (the drawer's external arrow). Default: a link.
   */
  pageMark?: ReactNode | false;
};

/** "in 2 days" · "in 5 h" · "in 20 min" · "3 days ago". */
export function relativeTime(iso: string, now = new Date()): string {
  const ms = new Date(iso).getTime() - now.getTime();
  const abs = Math.abs(ms);
  const min = Math.round(abs / 60_000);
  const text =
    min < 60
      ? `${Math.max(1, min)} min`
      : min < 60 * 36
        ? `${Math.round(min / 60)} h`
        : `${Math.round(min / 1440)} days`;
  return ms >= 0 ? `in ${text}` : `${text} ago`;
}

/** The instant in company time: "Fri, Oct 23, 5:00 PM EDT". */
export function companyTime(iso: string, timezone?: string | null): string {
  try {
    return new Date(iso).toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
      ...(timezone ? { timeZone: timezone } : {}),
    });
  } catch {
    return new Date(iso).toLocaleString("en-US");
  }
}

function useTimezone(): string | null {
  return useContext(SessionContext)?.company?.calendar?.timezone ?? null;
}

const SOURCE_WORD: Record<LastReport["source"], string> = {
  agent: "reported by an agent",
  manual: "entered",
  import: "imported",
  event: "counted from events",
  ledger: "summed from the cost ledger",
  task: "counted from a board's tasks",
};

/** "Next due in 2 days" / "Late" / "Blocked" / "Reported" for the duty, with its explanation. */
export function dutyBadge(
  r: ReportingStatus,
  timezone: string | null,
  now = new Date(),
): { label: string; title: string; tone: "danger" | "warning" | "success" | "neutral" } {
  const due = `${r.due_label} · next ${companyTime(r.next_due_at, timezone)}`;
  switch (r.state) {
    case "blocked":
      return {
        label: "Blocked",
        title: `Blocked: ${r.blocked_reason ?? "the duty cannot be met"}`,
        tone: "warning",
      };
    case "late":
      return {
        label: "Late",
        title: `Late: ${r.last_period_key ?? "the period"} was due ${companyTime(r.late_since ?? r.last_due_at ?? r.next_due_at, timezone)} and is not reported`,
        tone: "danger",
      };
    case "reported":
      return {
        label: "Reported",
        title: `${r.next_period_key} is reported · ${due}`,
        tone: "success",
      };
    default:
      return {
        label: `Due ${relativeTime(r.next_due_at, now)}`,
        title: `Next due ${companyTime(r.next_due_at, timezone)} (${r.next_period_key}) · ${r.due_label}`,
        tone: r.state === "due_soon" ? "warning" : "neutral",
      };
  }
}

function PendingReviewBadge({
  pending,
  pageMark,
}: {
  pending: PendingReview;
  pageMark?: ReactNode | false | undefined;
}) {
  const slug = useContext(SessionContext)?.company?.slug;
  const what = pending.value ?? pending.quantity;
  const text = `Pending review: ${what === null ? "a value" : what} for ${pending.period_key}${
    pending.count > 1 ? ` (${pending.count} values wait)` : ""
  }`;
  const badge = (
    <Badge variant="info" dot title={text} aria-label={text} data-slot="pending-review">
      Pending review
    </Badge>
  );
  if (!slug || pageMark === false) return badge;
  return (
    <Link
      to="/c/$slug/agents/approvals"
      params={{ slug }}
      search={
        ((prev: Record<string, unknown>) => ({
          ...keepFocus(prev),
          kind: "kpi_value",
        })) as never
      }
      className="inline-flex rounded-sm underline-offset-4 hover:underline"
      aria-label={`${text}: open the KPI reviews`}
      data-page-link={pageMark ? "" : undefined}
    >
      {badge}
      {pageMark || null}
    </Link>
  );
}

/** Badges for a metric row: Pending review, then the duty (Next due / Late / Blocked / Reported). */
export function ReportingStatusChip({
  reporting,
  lastReport,
  pendingReview,
  compact = false,
  pageMark,
}: ReportingStatusProps) {
  const timezone = useTimezone();
  if (!reporting && !pendingReview) return null;
  const b = reporting ? dutyBadge(reporting, timezone) : null;
  const urgent = reporting?.state === "late" || reporting?.state === "blocked";
  return (
    <span
      className="inline-flex min-w-0 flex-wrap items-center gap-1"
      data-slot="reporting-status"
      data-state={reporting?.state ?? "none"}
    >
      {pendingReview && (!compact || !urgent) ? (
        <PendingReviewBadge pending={pendingReview} pageMark={pageMark} />
      ) : null}
      {b && (!compact || !pendingReview || urgent) ? (
        <Badge variant={b.tone} title={b.title} aria-label={b.title} data-slot="duty-state">
          {b.label}
        </Badge>
      ) : null}
      {!compact && lastReport ? <AgentReportMark metric={{ last_report: lastReport }} /> : null}
    </span>
  );
}

/**
 * The full status as lines under a metric: the duty (Next due relative and absolute in company
 * time, or Late / Blocked with why), the last report and any value waiting for review.
 */
export function ReportingStatusLine({
  reporting,
  lastReport,
  pendingReview,
  compact = false,
}: ReportingStatusProps) {
  const timezone = useTimezone();
  if (!reporting && !lastReport && !pendingReview) return null;
  const b = reporting ? dutyBadge(reporting, timezone) : null;
  return (
    <span
      className="flex min-w-0 flex-col gap-1 text-xs font-normal text-muted-foreground"
      data-slot="reporting-status-line"
      data-state={reporting?.state ?? "none"}
    >
      {reporting && b ? (
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <Badge variant={b.tone} title={b.title} data-slot="duty-state">
            {b.label}
          </Badge>
          <span className="min-w-0 break-words">
            {reporting.state === "blocked"
              ? (reporting.blocked_reason ?? "The duty cannot be met.")
              : reporting.state === "late"
                ? `${reporting.last_period_key ?? "The period"} was due ${companyTime(reporting.late_since ?? reporting.last_due_at ?? reporting.next_due_at, timezone)}`
                : `Next due ${companyTime(reporting.next_due_at, timezone)} for ${reporting.next_period_key}`}
            {compact ? null : ` · ${reporting.due_label}`}
          </span>
        </span>
      ) : null}
      {lastReport ? (
        <span
          className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
          data-slot="last-report"
        >
          <span>
            Last report: <span className="text-foreground tabular-nums">{lastReport.value}</span>{" "}
            for {lastReport.period_key},{" "}
            {lastReport.provenance?.agent_name
              ? `reported by ${lastReport.provenance.agent_name}`
              : SOURCE_WORD[lastReport.source]}
            {lastReport.provenance?.review
              ? lastReport.provenance.review.state === "edited"
                ? ` (edited in review from ${lastReport.provenance.review.original_value ?? "—"})`
                : " (approved in review)"
              : ""}
          </span>
          <AgentReportMark metric={{ last_report: lastReport }} />
        </span>
      ) : null}
      {pendingReview ? (
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <PendingReviewBadge pending={pendingReview} />
          <span>
            {pendingReview.value ?? pendingReview.quantity} for {pendingReview.period_key} waits for
            a person's review
          </span>
        </span>
      ) : null}
    </span>
  );
}

/** The status props of any row that may carry them (a scorecard row; the chart's dims do not). */
export function reportingProps(m: object): Omit<ReportingStatusProps, "compact"> {
  const r = m as {
    reporting?: ReportingStatus | null;
    last_report?: LastReport | null;
    pending_review?: PendingReview | null;
  };
  return {
    reporting: r.reporting ?? null,
    lastReport: r.last_report ?? null,
    pendingReview: r.pending_review ?? null,
  };
}

/** Managers: "KPI reporting" for a seat (Setup › KPI reporting with the seat selected). */
export function KpiReportingLink({
  seatId,
  label = "KPI reporting",
}: {
  seatId: string;
  label?: string;
}) {
  const company = useContext(SessionContext)?.company;
  if (!company || !isManager(company)) return null;
  return (
    <Link
      to="/c/$slug/setup/kpi-reporting"
      params={{ slug: company.slug }}
      search={((prev: Record<string, unknown>) => ({ ...keepFocus(prev), seat: seatId })) as never}
      className={buttonVariants({ variant: "outline", size: "sm" })}
      data-slot="kpi-reporting-link"
    >
      <GaugeIcon aria-hidden="true" />
      {label}
    </Link>
  );
}
